import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid2";
import "./ProjectAboutPage.css";
import MapComponent from "../../Components/MapComponent/MapComponent";
import MiddleDropdown from "../../Components/MiddleDropdown/MiddleDropdown";
import { Link, useLocation, useParams } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { InfoOutlined as InfoIcon } from "@mui/icons-material";
import LoaderComponent from "../../Components/LoaderComponent/LoaderComponent";
import { getProject, getProjectQuarterChangesFromLastQuarter, getProjectStages ,MEDIA_BASE_URL } from "../../Services/api";
import HalfProgressComponent from "../../Components/HalfProgressComponent/HalfProgressComponent";
import { motion, Variants } from "framer-motion";
import { formatMonthYear } from "../../Utils/date";
import { downloadQuarterlyReportExcel } from "../../Utils/downloadQuarterlyReportExcel";
import { slugify } from "../../Utils/url";

const variants: Variants = {
  open: {
    height: "auto",
    opacity: 1,
    transition: { duration: 0.5, ease: "easeInOut" },
  },
  collapsed: {
    height: 0,
    opacity: 0,
    transition: { duration: 0.5, ease: "easeInOut" },
  },
};

interface Props { }

const ProjectAboutPage = (props: Props) => {
  const { id } = useParams();
  const { state } = useLocation();

  const [project, setProject] = useState<any>(null);
  const [projectContacts, setProjectContacts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDropdown, setSelectedDropdown] = useState<string | null>("desc");
  const [downloadStatusText, setDownloadStatusText] = useState("בודק זמינות נתונים...");

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    if (state && state.project) {
      setProject(state.project);
      document.title = "תשתיות לישראל | " + state.project.name;
      setLoading(false);
    } else {
      getProject(id)
        .then((projectData) => {
          setProject(projectData);
          document.title = "תשתיות לישראל | " + projectData.name;
          const contactsArr: any[] = [];
          projectData.contacts.forEach((contact: any) => {
            contactsArr.push([contact.name, contact.role, contact.email, contact.phone].filter((str) => !!str).join(" - "));
          });
          setProjectContacts(contactsArr);
        })
        .catch((err) => {
          setError(err.message || "Unknown error");
        })
        .finally(() => {
          setLoading(false);
        });
    }

    const resetFocus = document.getElementById("reset-focus");
    if (resetFocus) resetFocus.focus();
  }, [id]);

  useEffect(() => {
    if (!id) return;

    const checkAvailability = async () => {
      try {
        const data = await getProjectQuarterChangesFromLastQuarter(id);
        if (Array.isArray(data) && data.length === 0) {
          setDownloadStatusText("אין נתונים זמינים להורדה");
        } else {
          setDownloadStatusText("לחץ כאן להורדת דו''ח השינויים");
        }
      } catch (e) {
        console.error("שגיאה בבדיקת זמינות נתונים", e);
        setDownloadStatusText("שגיאה בבדיקת נתונים");
      }
    };

    checkAvailability();
  }, [id]);

  if (loading) return <LoaderComponent />;
  if (error) return <p>Error: {error}</p>;

  return (
    <Box className="green-light-page-wrapper">
      <motion.div
        key={window.location.pathname}
        variants={variants}
        initial="collapsed"
        animate="open"
        exit="collapsed"
        style={{ overflow: "hidden" }}
      >
        <div className="main-card">
          <Box className="top-bar">
            <Box className="right-texts">
              <p className="section-title">אודות המיזם</p>
              <h1 className="subtitle">{project.name}</h1>
            </Box>

            <Box bgcolor={"#1293FE"} borderRadius={"1rem"} padding={"0.8rem"} display={"flex"} gap={"10px"}>
              <InfoIcon
                style={{
                  color: "#FFF",
                  width: 24,
                  height: 24,
                }}
              />
              <Typography color={"white"} fontWeight={600}>
                תאריך סיום משוער: {project.updated_end_date === "TBD" ? "טרם נקבע" : formatMonthYear(project.updated_end_date)}
              </Typography>
            </Box>
          </Box>
          <Grid
            container
            spacing={2}
            sx={{
              flex: 1,
              height: "100%",
              overflow: "hidden",
              paddingInline: { sm: "40.27px" },
              paddingTop: "24.61px",
              paddingBottom: "14.32px",
            }}
          >
            {/* Right Column */}
            <Grid
              size={{ xs: 12, md: 4 }}
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <div className={isMobile ? "" : "blue-card"}>
                <div className={isMobile ? "" : "blue-card-section right"}>
                  <Box
                    sx={{
                      background: "#FFFFFF",
                      width: "100%",
                      borderRadius: "25px",
                      py: 1,
                      px: 3,
                      textAlign: "center",
                    }}
                  >
                    <Typography variant={"h2"} sx={{ color: "#0C3768", fontSize: "24px", fontWeight: 700, mb: 2 }}>
                      עמידה בזמנים:
                    </Typography>
                    <HalfProgressComponent data={project} currentStage={project.current_stage.stage_number} radius={100} stroke={15} />
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: project.days_late === 0 ? (project.updated_end_date === "TBD" ? "rgb(255, 189, 6)" : "#5ECCA3") : "#FF6B54",
                        borderRadius: "25px",
                        py: 1,
                        px: 3,
                        mt: 2,
                      }}
                    >
                      {project.days_late > 0 ? (
                        <div>
                          <Typography component={"div"} sx={{ fontSize: 19, fontWeight: 700, color: "#FFFFFF" }}>
                            בעיכוב של:
                          </Typography>
                          <Typography component={"div"} sx={{ fontSize: 35, fontWeight: 700, color: "#FFFFFF" }}>
                            {project.updated_end_date === "TBD" ? "טרם נקבע" : project.days_late + " ימים"}
                          </Typography>
                        </div>
                      ) : (
                        <div>
                          <Typography component={"div"} sx={{ fontSize: 35, fontWeight: 700, color: "#FFFFFF" }}>
                            {project.updated_end_date === "TBD" ? "טרם נקבע" : "אין עיכוב"}
                          </Typography>
                        </div>
                      )}
                    </Box>
                  </Box>
                </div>
                <Box className={isMobile ? "mobile-left" : "blue-card-section left"}>
                  {/* Section 1 - Calendar */}
                  <div className="left-section-group">
                    <div className="left-section-title">
                      <img src={isMobile ? "/images/calendar-mobile.png" : "/images/calander.png"} alt="calendar" className="left-icon" />
                      <p className="title-text">תאריכי התחלה וסיום המיזם</p>
                    </div>
                    <p className="highlight-text">
                      תאריך סיום עדכני: {project.updated_end_date === "TBD" ? "טרם נקבע" : formatMonthYear(project.updated_end_date)}
                    </p>
                    <p className="highlight-text">תאריך התחלה עדכני: {formatMonthYear(project.updated_start_date)}</p>
                    <p className="normal-text">
                      תאריך סיום מקורי: {project.original_end_date === "TBD" ? "טרם נקבע" : formatMonthYear(project.original_end_date)}
                    </p>
                    <p className="normal-text">תאריך התחלה מקורי: {formatMonthYear(project.original_start_date)}</p>
                    <p className="normal-text">דיווח אחרון: {project.last_reported_date}</p>
                    <hr className="left-divider" />
                  </div>

                  {/* Section 2 - Tools */}
                  <div className="left-section-group">
                    <div className="left-section-title">
                      <img src={isMobile ? "/images/tool-mobile.png" : "/images/tool.png"} alt="" className="left-icon" />
                      <p className="title-text">שלב נוכחי</p>
                    </div>
                    <p className="stage-description">{project.current_stage.description}</p>
                  </div>
                </Box>
              </div>{" "}
              {!isMobile && (
                <Box className="media-container">
                  {project.media_type === "video" ? (
                    <video controls className="media-video">
                      <source
                        //On prod src={`${MEDIA_BASE_URL}/${project.media_url}`}
                        src={`${MEDIA_BASE_URL}/${project.media_url}`}
                        type="video/mp4"
                      />
                      Your browser does not support the video tag.
                    </video>
                  ) : (
                    <img
                      //On prod src={`${MEDIA_BASE_URL}/${project.media_url}`}
                      src={`${MEDIA_BASE_URL}/${project.media_url}`}
                      alt=""
                      className="media-image"
                    />
                  )}
                </Box>
              )}
            </Grid>

            {/* Middle Column */}
            <Grid
              size={{ xs: 12, md: 3 }}
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
                overflowY: "auto",
                gap: "6px",
              }}
            >
              <MiddleDropdown
                title="תיאור המיזם"
                body={project.description}
                icon="/images/exclamation_mark.png"
                isExpanded={selectedDropdown === "desc"}
                onClick={() => setSelectedDropdown(selectedDropdown === "desc" ? null : "desc")}
              />
              <MiddleDropdown
                title="משרד אחראי"
                body={project.office}
                isObject
                icon="/images/office_logo.png"
                isExpanded={selectedDropdown === "office"}
                onClick={() => setSelectedDropdown(selectedDropdown === "office" ? null : "office")}
              />
              <MiddleDropdown
                title="גוף מבצע"
                body={project.project_companies}
                isObject
                icon="/images/tools_logo.png"
                isExpanded={selectedDropdown === "company"}
                onClick={() => setSelectedDropdown(selectedDropdown === "company" ? null : "company")}
              />
              <MiddleDropdown
                title="פרטי התקשרות"
                body={projectContacts}
                isObject
                icon="/images/conv_logo.png"
                isExpanded={selectedDropdown === "contacts"}
                onClick={() => setSelectedDropdown(selectedDropdown === "contacts" ? null : "contacts")}
              />
              <MiddleDropdown
                title="הורדת דו''ח שינויים"
                body={{
                  ['להלן דו"ח שמשווה בין הדיווח ברבעון האחרון שדווח במיזם לבין הרבעון לפניו. בדו"ח יופיעו רק שינויים בין הדיווחים.']: "text",
                  [downloadStatusText]: "download",
                }}
                isObject
                icon="/images/download-icon-blue.png"
                isExpanded={selectedDropdown === "download"}
                onClick={() => {
                  setSelectedDropdown(selectedDropdown === "download" ? null : "download");
                }}
                onBodyItemClick={(key) => {
                  if (key.includes("לחץ כאן להורדת") && id) {
                    downloadQuarterlyReportExcel(id);
                  }
                }}
                hasDataToDownload={downloadStatusText.includes("לחץ כאן להורדת")}
              />
              <MiddleDropdown
                title="מידע חשוב"
                body={
                  'לוחות הזמנים המפורסמים באתר זה הם לוחות זמנים משוערים בלבד, שפורסמו על ידי גופי הביצוע הרלוונטיים בהתאם לסעיף 43 לחוק לקידום תשתיות לאומיות, התשפ"ג-2023. מובהר כי לוחות הזמנים נתונים לשינויים, לרבות בשל אילוצים תכנוניים, רגולטוריים, תקציביים, תפעוליים או אחרים.'
                }
                icon="/images/exclamation_mark.png"
                isExpanded={selectedDropdown === "info"}
                onClick={() => setSelectedDropdown(selectedDropdown === "info" ? null : "info")}
              />
            </Grid>

            {/* Left Column */}
            <Grid
              size={{ xs: 12, md: 5 }}
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                overflow: "hidden",
              }}
            >
              <div className="stacked-card-container">
                <div className="white-card">
                  <MapComponent
                    layerNumber={(() => {
                      return project?.project_tag;
                    })()}
                    activeLayerNumber={project?.project_layer}
                  />
                </div>
              </div>
            </Grid>
          </Grid>
        </div>
      </motion.div>
      <Link
        state={{ project: project }}
        style={{
          textDecoration: "none",
          color: "inherit",
          cursor: "pointer",
          boxShadow: "0 0 50px rgba(0,0,0,0.1)",
          position: "sticky",
          bottom: 0,
          borderRadius: "26px 26px 0 0",
        }}
        to={`/project/details/${id}/${slugify(project.name)}`}
      >
        <div className="middle-sticky-card">
          <img src="/images/arrow_up.png" alt="up arrow" className="sticky-arrow" />
          <p className="sticky-title">לו"ז המיזם</p>
        </div>
      </Link>{" "}
    </Box>
  );
};

export default ProjectAboutPage;
